import { type FC, Fragment, useState } from 'react'
import { Collapse, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
// eslint-disable-next-line no-restricted-imports -- Use sx props or styled components to create CSS
import { ExpandLess, ExpandMore } from '@dhl/icons'

import { SideMenuListItem, styles } from './SideMenuListItem.js'
import { type MenuItemWithSubItems } from './types.js'
import { SideMenuInline } from './SideMenuInline.js'

type Props = {
  disableTab: boolean
  isExpanded: boolean
  toggleExpanded?: () => void
  menuItems: MenuItemWithSubItems[]
  navigationPath?: string
}

const isActive = (navigationPath: string | undefined, link: string | undefined): boolean => {
  if (!navigationPath || !link) {
    return false
  }
  return navigationPath.startsWith(link)
}

export const SideMenuWithCollapsedSubmenu: FC<Props> = ({
  disableTab,
  isExpanded,
  menuItems,
  navigationPath,
  toggleExpanded,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const setGroupExpanded = (key: string, expanded: boolean) => {
    setExpandedGroups((groups) => ({ ...groups, [key]: expanded }))
  }

  return (
    <>
      {menuItems.map((item) => {
        if (item.subMenuItems) {
          const isActiveSub = item.subMenuItems.some(
            (subItem) => subItem.active ?? isActive(navigationPath, subItem.link)
          )
          const isOpen = expandedGroups[item.key] ?? (isActiveSub || item.defaultExpanded || false)

          return (
            <Fragment key={item.key}>
              {isExpanded && (
                <ListItem id={item.id} onClick={() => setGroupExpanded(item.key, !isOpen)} sx={{ cursor: 'pointer' }}>
                  {item.icon && !item.hideIconWhenExpanded && (
                    <ListItemIcon sx={{ pl: '4px', my: '8px' }}>{item.icon}</ListItemIcon>
                  )}
                  <ListItemText primary={<Typography>{item.label}</Typography>} disableTypography />

                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
              )}
              {!isExpanded && (
                <ListItemIcon
                  sx={
                    isActiveSub
                      ? { px: '18px', my: '18px', ...styles.activeMenuItem }
                      : { px: '18px', my: '18px', ...styles.menuItem }
                  }
                  onClick={() => {
                    toggleExpanded?.()
                    setGroupExpanded(item.key, true)
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}

              <Collapse in={isOpen && isExpanded} timeout="auto">
                <List
                  component="div"
                  role="group"
                  aria-label={typeof item.label === 'string' ? item.label : undefined}
                  disablePadding
                  sx={{
                    bgcolor: 'action.hover',
                    pb: 2,
                    '& .MuiListItemButton-root': {
                      pl: 4,
                    },
                  }}
                >
                  <SideMenuInline
                    disableTab={disableTab}
                    isExpanded={isExpanded}
                    menuItems={item.subMenuItems}
                    navigationPath={navigationPath}
                  />
                </List>
              </Collapse>
            </Fragment>
          )
        }

        return (
          <SideMenuListItem
            isExpanded={isExpanded}
            key={item.key}
            disableTab={disableTab}
            menuItem={{
              ...item,
              active: item.active === undefined ? isActive(navigationPath, item.link) : item.active,
            }}
          />
        )
      })}
    </>
  )
}
